import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FAQSection from "../components/FAQSection";

const CASES = [
  {
    id: "ai-attrition",
    tag: "Retention · AI Start-up",
    size: "28-person team · Bellandur",
    title: "Unmasking the hub behind 14% AI-team attrition.",
    story:
      "A deep-tech team lost four engineers to one competitor in six weeks. The CEO saw a number. We saw a pattern — every exit had reported to the same quietly-departed tech lead. In ten days we rebuilt the management tree and closed the bleed.",
    metrics: [
      ["0", "Regrettable exits in the next quarter"],
      ["94%", "Stay-interview trust score"],
      ["3.2×", "Internal referral rate after rebuild"],
    ],
  },
  {
    id: "scale-up-culture",
    tag: "Culture · AI-native Scale-up",
    size: "80 → 220 employees · Koramangala",
    title: "Rebuilding the operating system after Series B.",
    story:
      "An AI-native product team tripled headcount within a year of closing its Series B. Standups 'felt weird'. We mapped the culture graph, redesigned pod composition and installed a six-ritual operating cadence. Six months later, eNPS recovered to founder-era levels.",
    metrics: [
      ["+34 pts", "eNPS lift in 6 months"],
      ["87%", "Feel genuinely heard (pulse)"],
      ["+4 hrs", "Avg weekly deep-work reclaimed"],
    ],
  },
  {
    id: "pms-cadence",
    tag: "Performance · Product Company",
    size: "140 employees · Indiranagar",
    title: "Pausing a PMS rollout to fix the 1:1 cadence first.",
    story:
      "A new performance system was three weeks from launch. Our diagnose sprint found managers were doing 1:1s every 11 weeks. We paused the rollout, rebuilt the weekly rhythm, then shipped a lighter PMS on top.",
    metrics: [
      ["11w → 2w", "Median 1:1 cadence"],
      ["89%", "Managers rate review as 'useful'"],
      ["+41%", "Growth conversations documented"],
    ],
  },
  {
    id: "comp-bubble",
    tag: "Rewards · Deep-tech",
    size: "60 employees · ORR",
    title: "Holding the comp band without blowing up the runway.",
    story:
      "An ML lead received a 2.4× offer. Two senior engineers were quietly interviewing. Instead of matching headline salary, we designed a refresher-grant policy tied to a two-year cliff. Closed the gap, preserved the band.",
    metrics: [
      ["0", "Regrettable senior exits"],
      ["9/10", "Trust in comp philosophy (pulse)"],
      ["2.4×", "Equity-transparency score"],
    ],
  },
];

export default function Impact() {
  return (
    <div data-testid="impact-page" className="bg-[#050505] text-white min-h-screen">
      <Header />
      <main>
        <section className="relative pt-32 md:pt-40 pb-16 grid-bg overflow-hidden">
          <div className="absolute inset-0 beam pointer-events-none" />
          <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-6 rise d-1">
              / Impact
            </div>
            <h1
              data-testid="impact-headline"
              className="font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-white max-w-5xl rise d-2"
            >
              Case studies
              <span className="block text-cy">from the Bangalore floor.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-zinc-400 text-base md:text-lg rise d-3">
              Four representative engagements — names anonymised, numbers real. Each one started
              with a sharp diagnosis and ended with a durable operating change.
            </p>
          </div>
        </section>

        <section className="bg-[#070709] border-t border-ink-600">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-24 space-y-6">
            {CASES.map((c, i) => (
              <article
                key={c.id}
                data-testid={`case-${c.id}`}
                className="group relative border border-ink-500 bg-[#050505] hover:border-cy/60 transition-colors"
              >
                <div className="grid grid-cols-12 gap-0">
                  <div className="col-span-12 md:col-span-2 p-6 md:p-8 md:border-r border-ink-500">
                    <div className="font-mono text-xs text-cy">/ {String(i + 1).padStart(2, "0")}</div>
                    <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                      {c.tag}
                    </div>
                    <div className="mt-2 font-mono text-[10px] text-zinc-600">{c.size}</div>
                  </div>

                  <div className="col-span-12 md:col-span-6 p-6 md:p-8 md:border-r border-ink-500">
                    <h2 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight leading-snug max-w-xl">
                      {c.title}
                    </h2>
                    <p className="mt-4 text-sm md:text-base text-zinc-400 leading-relaxed max-w-xl">
                      {c.story}
                    </p>
                  </div>

                  <div className="col-span-12 md:col-span-4 p-6 md:p-8">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">
                      Outcomes
                    </div>
                    <ul className="space-y-4">
                      {c.metrics.map(([k, v]) => (
                        <li key={v} className="flex items-end justify-between gap-4 border-b border-ink-500 pb-3">
                          <span className="font-display text-2xl md:text-3xl font-black text-cy tracking-tight leading-none">
                            {k}
                          </span>
                          <span className="text-xs text-zinc-400 text-right max-w-[60%]">{v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <FAQSection
          faqs={[
            {
              q: "Can you share client names or references?",
              a: "We work under strict confidentiality. Most engagements are with founder-led companies where people decisions carry signal. With explicit client consent we're happy to share one or two references on a discovery call — not in public marketing.",
            },
            {
              q: "How long until we see a measurable shift?",
              a: "A Diagnose sprint takes 2 to 4 weeks. Most clients feel the first operational shift — in 1:1 cadence, intake quality or manager confidence — within 30 to 45 days of engagement.",
            },
            {
              q: "What metrics do you typically track?",
              a: "We favour leading indicators that today's leaders actually trust — psychological-safety pulse, belonging index, stay-interview trust, growth-conversation coverage, manager-trust score — alongside traditional attrition, eNPS and time-to-productivity.",
            },
            {
              q: "Do you guarantee outcomes?",
              a: "We offer outcome-linked pricing where the metric is clean and under our influence. We don't guarantee what we can't control — but we commit to the operating cadence that moves those metrics in the right direction.",
            },
            {
              q: "How do you preserve anonymity in employee listening?",
              a: "All stay interviews, pulses and focus groups are facilitated by HumInt and reported only in aggregate to leadership. No individual quote leaves the room without explicit consent. This is non-negotiable.",
            },
          ]}
          eyebrow="05 · Questions"
        />
      </main>
      <Footer />
    </div>
  );
}
